import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Save, Eye, RotateCcw, Send, FileText, CheckCircle2, Globe, Building2,
  AlertCircle, Users, Code,
} from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_LABEL = {
  employee: "Colaborador",
  hr: "RH / Admin",
  operator: "Operador",
  candidate: "Candidato",
};

export default function EmailTemplatesPanel({ companies = [], isMaster }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeCompany, setScopeCompany] = useState(isMaster ? "global" : (companies[0]?.id || ""));
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // Default scope when companies list arrives (rh_admin)
  useEffect(() => {
    if (!isMaster && !scopeCompany && companies[0]) setScopeCompany(companies[0].id);
  }, [companies, isMaster, scopeCompany]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = scopeCompany ? { company_id: scopeCompany } : {};
      const { data } = await api.get("/admin/emails/templates", { params });
      setTemplates(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar modelos");
    } finally { setLoading(false); }
  }, [scopeCompany]);

  useEffect(() => { if (scopeCompany) loadList(); }, [scopeCompany, loadList]);

  const openEditor = async (tpl) => {
    try {
      const params = scopeCompany ? { company_id: scopeCompany } : {};
      const { data } = await api.get(`/admin/emails/templates/${tpl.key}`, { params });
      setSelected(data);
      setEditor({
        subject: data.resolved.subject,
        html: data.resolved.html,
        text: data.resolved.text,
        active: data.resolved.active !== false,
      });
      setPreview(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao abrir modelo");
    }
  };

  const closeEditor = () => { setSelected(null); setEditor(null); setPreview(null); };

  const doPreview = async () => {
    if (!selected || !editor) return;
    try {
      const { data } = await api.post(
        `/admin/emails/templates/${selected.key}/preview`,
        { subject: editor.subject, html: editor.html, text: editor.text }
      );
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha no preview");
    }
  };

  const doSave = async () => {
    if (!selected || !editor) return;
    setSending(true);
    try {
      const body = {
        company_id: scopeCompany === "global" ? null : scopeCompany,
        subject: editor.subject,
        html: editor.html,
        text: editor.text,
        active: editor.active,
      };
      await api.put(`/admin/emails/templates/${selected.key}`, body);
      toast.success("Modelo salvo");
      await loadList();
      closeEditor();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao salvar");
    } finally { setSending(false); }
  };

  const doReset = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const params = scopeCompany === "global" ? {} : { company_id: scopeCompany };
      await api.post(`/admin/emails/templates/${selected.key}/reset`, {}, { params });
      toast.success(scopeCompany === "global" ? "Modelo global restaurado para o padrão" : "Personalização da empresa removida (voltou ao modelo global)");
      setConfirmReset(false);
      await loadList();
      closeEditor();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao restaurar");
    } finally { setSending(false); }
  };

  const doSendTest = async () => {
    if (!selected || !testEmail) return;
    setSending(true);
    try {
      const body = { to: testEmail, company_id: scopeCompany === "global" ? null : scopeCompany };
      const { data } = await api.post(`/admin/emails/templates/${selected.key}/test`, body);
      if (data?.ok) toast.success(`E-mail de teste enviado para ${testEmail}`);
      else toast.error(`Falha: ${data?.error || "desconhecida"}`);
      setTestOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao enviar teste");
    } finally { setSending(false); }
  };

  const scopeLabel = useMemo(() => {
    if (scopeCompany === "global") return "Modelo global (padrão para todas as empresas)";
    const c = companies.find(x => x.id === scopeCompany);
    return c ? `Empresa: ${c.name || c.nome_fantasia || c.razao_social}` : "Selecione uma empresa";
  }, [scopeCompany, companies]);

  return (
    <div className="space-y-5" data-testid="email-templates-panel">
      {/* Scope selector */}
      <div className="rounded-xl border border-[#2C3A2F] bg-[#1A231F] p-4 flex items-center gap-4">
        <div className="flex items-center gap-2 text-sm">
          {scopeCompany === "global" ? <Globe className="w-4 h-4 text-[#C5A059]" /> : <Building2 className="w-4 h-4 text-[#C5A059]" />}
          <span className="text-[#AAB4A8]">Escopo:</span>
          <span className="font-medium">{scopeLabel}</span>
        </div>
        <div className="ml-auto w-72">
          <Select value={scopeCompany} onValueChange={setScopeCompany}>
            <SelectTrigger data-testid="templates-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {isMaster && <SelectItem value="global">🌐 Modelo global (padrão)</SelectItem>}
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name || c.nome_fantasia || c.razao_social}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Templates list */}
      <div className="rounded-xl border border-[#2C3A2F] bg-[#1A231F] overflow-hidden">
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#C5A059]" /></div>
        ) : (
          <div className="divide-y divide-[#2C3A2F]">
            {templates.map(t => (
              <div key={t.key} className="p-4 hover:bg-[#C5A05908] flex items-start gap-4" data-testid="email-template-row">
                <div className="w-10 h-10 rounded-lg bg-[#C5A05915] border border-[#C5A05940] flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4 text-[#C5A059]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium">{t.label}</h3>
                    <Badge variant="outline" className="text-[10px]"><Users className="w-3 h-3 mr-1" />{AUDIENCE_LABEL[t.audience] || t.audience}</Badge>
                    {t.has_tenant_override && scopeCompany !== "global" && (
                      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Personalizado
                      </Badge>
                    )}
                    {!t.active && <Badge className="bg-red-500/15 text-red-300 border-red-500/30 text-[10px]">Desativado</Badge>}
                    <Badge variant="outline" className="text-[10px] text-[#AAB4A8]">
                      origem: {t.resolved_source}
                    </Badge>
                  </div>
                  <p className="text-xs text-[#AAB4A8] mt-1 truncate">{t.description}</p>
                  <div className="text-xs text-[#AAB4A8] mt-1">
                    Assunto: <span className="text-white/80 font-mono text-[11px]">{t.subject}</span>
                  </div>
                  {t.updated_at && (
                    <div className="text-[10px] text-[#AAB4A8] mt-1">
                      Última edição: {new Date(t.updated_at).toLocaleString("pt-BR")} {t.updated_by_name ? `por ${t.updated_by_name}` : ""}
                    </div>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => openEditor(t)} data-testid={`template-edit-${t.key}`}>
                  Editar
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && closeEditor()}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#C5A059]" />
              {selected?.label}
              {!isMaster && <Badge variant="outline" className="ml-2 text-[10px]"><Building2 className="w-3 h-3 mr-1" />Escopo: Empresa</Badge>}
              {isMaster && scopeCompany === "global" && <Badge className="bg-[#C5A05920] text-[#C5A059] border-[#C5A05940] text-[10px] ml-2"><Globe className="w-3 h-3 mr-1" />Modelo Global</Badge>}
            </DialogTitle>
          </DialogHeader>
          {selected && editor && (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Assunto</label>
                    <div className="flex items-center gap-2 text-xs">
                      <Switch id="active-switch" checked={editor.active} onCheckedChange={v => setEditor(e => ({ ...e, active: v }))} data-testid="template-active-switch" />
                      <label htmlFor="active-switch" className="cursor-pointer">{editor.active ? "Ativo" : "Desativado"}</label>
                    </div>
                  </div>
                  <SubjectInputWithVars value={editor.subject} onChange={v => setEditor(e => ({ ...e, subject: v }))} variables={selected.variables} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8] flex items-center gap-2"><Code className="w-3 h-3" /> HTML</label>
                  <HtmlEditorWithVars value={editor.html} onChange={v => setEditor(e => ({ ...e, html: v }))} variables={selected.variables} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Texto puro (fallback)</label>
                  <Textarea rows={5} value={editor.text || ""} onChange={e => setEditor(ed => ({ ...ed, text: e.target.value }))} className="font-mono text-xs" data-testid="template-text-input" />
                </div>
              </div>
              <VariablesList variables={selected.variables} />
            </div>
          )}
          {preview && (
            <div className="mt-4 border border-[#2C3A2F] rounded-xl overflow-hidden">
              <div className="bg-[#0B140F] px-4 py-2 text-xs text-[#AAB4A8] flex items-center justify-between">
                <span>Preview com dados de exemplo</span>
                <span className="font-mono">{preview.subject}</span>
              </div>
              <iframe title="preview" srcDoc={preview.html} className="w-full h-96 bg-white" />
            </div>
          )}
          <DialogFooter className="gap-2 mt-4 flex-wrap">
            <Button variant="outline" onClick={() => setConfirmReset(true)} data-testid="template-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restaurar padrão
            </Button>
            <Button variant="outline" onClick={doPreview} data-testid="template-preview-btn">
              <Eye className="w-3.5 h-3.5 mr-1.5" /> Pré-visualizar
            </Button>
            <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="template-test-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Enviar teste
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" onClick={closeEditor}>Cancelar</Button>
            <Button onClick={doSave} disabled={sending} data-testid="template-save-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset confirmation */}
      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-amber-400" /> Restaurar modelo padrão?</AlertDialogTitle>
            <AlertDialogDescription>
              {scopeCompany === "global"
                ? "Isso removerá a personalização global e voltará ao modelo padrão do sistema. As personalizações por empresa permanecem intactas."
                : "Isso removerá apenas a personalização desta empresa. O modelo global continua ativo e será usado nos próximos envios."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doReset} data-testid="template-reset-confirm">Restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Test send dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Enviar e-mail de teste</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8]">Destinatário</label>
              <Input type="email" placeholder="voce@empresa.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="template-test-email" />
            </div>
            <p className="text-xs text-[#AAB4A8]">O modelo será renderizado com dados de exemplo e enviado via Resend. O envio é registrado no Health Check.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestOpen(false)}>Cancelar</Button>
            <Button onClick={doSendTest} disabled={sending || !testEmail} data-testid="template-test-confirm">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Variables sidebar (click to insert) =====
const VariablesList = ({ variables }) => {
  return (
    <div className="rounded-xl border border-[#2C3A2F] bg-[#0B140F] p-3 h-fit sticky top-4">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#AAB4A8] mb-2">Variáveis disponíveis</div>
      <p className="text-xs text-[#AAB4A8] mb-3">Clique para copiar. Cole ou use o botão de inserção no assunto/HTML.</p>
      <div className="space-y-1 max-h-[60vh] overflow-y-auto">
        {variables.map(v => (
          <button key={v} className="w-full text-left px-2 py-1.5 rounded font-mono text-xs text-[#E7CD8D] bg-[#C5A05910] border border-[#C5A05925] hover:bg-[#C5A05920] transition"
                  onClick={() => { navigator.clipboard?.writeText(`{{${v}}}`); toast.success(`{{${v}}} copiado`); }}
                  data-testid={`var-copy-${v}`}>
            {"{{"}{v}{"}}"}
          </button>
        ))}
      </div>
    </div>
  );
};

// ===== Subject input with inline insertion =====
const SubjectInputWithVars = ({ value, onChange, variables }) => {
  const ref = useRef(null);
  const insert = (v) => {
    const el = ref.current;
    if (!el) return;
    const [s, e] = [el.selectionStart || value.length, el.selectionEnd || value.length];
    const next = value.slice(0, s) + `{{${v}}}` + value.slice(e);
    onChange(next);
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = s + v.length + 4; }, 0);
  };
  return (
    <div className="relative">
      <Input ref={ref} value={value} onChange={e => onChange(e.target.value)} className="pr-28" data-testid="template-subject-input" />
      <InlineInsertMenu variables={variables} onPick={insert} />
    </div>
  );
};

// ===== HTML editor (textarea) with inline insertion =====
const HtmlEditorWithVars = ({ value, onChange, variables }) => {
  const ref = useRef(null);
  const insert = (v) => {
    const el = ref.current;
    if (!el) return;
    const [s, e] = [el.selectionStart || value.length, el.selectionEnd || value.length];
    const next = value.slice(0, s) + `{{${v}}}` + value.slice(e);
    onChange(next);
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = s + v.length + 4; }, 0);
  };
  return (
    <div className="relative">
      <Textarea ref={ref} value={value} rows={14} onChange={e => onChange(e.target.value)} className="font-mono text-xs pr-28" data-testid="template-html-input" />
      <div className="absolute top-2 right-2">
        <InlineInsertMenu variables={variables} onPick={insert} />
      </div>
    </div>
  );
};

const InlineInsertMenu = ({ variables, onPick }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="absolute right-2 top-1/2 -translate-y-1/2 lg:static lg:translate-y-0">
      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(o => !o)} data-testid="var-insert-trigger">
        + Variável
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 bg-[#1A231F] border border-[#2C3A2F] rounded-lg shadow-xl z-20 min-w-[220px] max-h-64 overflow-y-auto">
          {variables.map(v => (
            <button key={v} className="w-full text-left px-3 py-1.5 text-xs font-mono text-[#E7CD8D] hover:bg-[#C5A05915]"
                    onClick={() => { onPick(v); setOpen(false); }}>
              {"{{"}{v}{"}}"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
